import "../../../assets/scss/instance-card-style.scss";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";

import { Button, message, Space, Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import { getFilesListFromGoogleDrive } from "../../server/GoogleDrive";

import { useAppDispatch, useAppSelector } from "../../store/hooks";
import FileUploadModal from "../../components/FileUploadMoal";

import {
  addToCurrList,
  backtoBaseStructure,
  updateCurrList,
  updateFolderStructure,
  updateNextPageToken,
} from "../../features/cloud/cloudSlice";
import GoogleCloudFileList from "../../components/GoogleCloudFileList";
import DockerFileList from "../../components/DockerFileList";
import InstanceSelectModal from "../../components/InstanceSelectModal";
import LoadingModal from "../../components/LoadingModal";
import {
  getAccessTokenSession,
  getGoogleDriveState,
  setGoogleDriveState,
  clearGoogleDriveState,
} from "../../utility/common/session-store";

const baseURL = process.env.REACT_APP_API_BASE_URL;

export const Cloud = () => {
  const { t } = useTranslation();
  const [baseFileList, setBaseFileList] = useState<any>([]);
  const [basePageToken, setBasePageToken] = useState<any>("");
  const [fileListLoading, setFileListLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [fileUploadModal, setFileUploadModal] = useState<boolean>(false);
  const [googleList, setGoogleList] = useState<boolean>(true);
  const [instanceModalState, setInstanceModalState] = useState<boolean>(false);
  const [navigating, setNavigating] = useState<boolean>(false);
  const [messageApi, contextHolder] = message.useMessage();

  const [searchParams] = useSearchParams();
  const state = searchParams.get("state");
  const prevState = getGoogleDriveState();

  const navigate = useNavigate();
  const fileList = useAppSelector((state: any) => state.cloud.currList);
  const dockerList = useAppSelector((state: any) => state.cloud.dockerList);
  const folderStructure = useAppSelector(
    (state: any) => state.cloud.folderStructure
  );
  const nextPageToken = useAppSelector(
    (state: any) => state.cloud.nextPageToken
  );
  const dispatch = useAppDispatch();
  const navigationIdRef = useRef(0);

  const getFiles = async (driveState: string | null) => {
    setInitialLoading(true);
    try {
      const res = await getFilesListFromGoogleDrive(driveState, {
        page_size: 20,
        page_token: "",
        search_query: searchQuery
          ? `name%20contains%20%22${searchQuery}%22`
          : "",
      });
      if (res.data.code === 0) {
        if (driveState) {
          setGoogleDriveState(driveState);
        }
        baseFileList.length === 0 && setBaseFileList(res.data.files);
        basePageToken === "" && setBasePageToken(res.data.next_page_token);
        dispatch(updateCurrList(res.data.files));
        dispatch(updateNextPageToken(res.data.next_page_token));
        dispatch(backtoBaseStructure());
        setIsConnected(true);
      } else {
        clearGoogleDriveState();
        setIsConnected(false);
      }
    } catch (err) {
      console.log(err);
      clearGoogleDriveState();
      setIsConnected(false);
    } finally {
      setInitialLoading(false);
    }
  };

  const getNextPageFiles = async () => {
    const driveState = getGoogleDriveState();
    setFileListLoading(true);
    try {
      const res = await getFilesListFromGoogleDrive(driveState, {
        page_size: 20,
        page_token: nextPageToken,
        search_query: searchQuery
          ? `name%20contains%20%22${searchQuery}%22`
          : "",
      });
      if (res.data.code === 0) {
        dispatch(addToCurrList(res.data.files));
        dispatch(updateNextPageToken(res.data.next_page_token));
      }
    } catch (err) {
      dispatch(updateNextPageToken(""));
      setIsConnected(false);
      navigate("/cloud");
    } finally {
      setFileListLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const token = getAccessTokenSession();
      const res = await axios.get(baseURL + "auth/drive/connect", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
      });
      if (res.data.code === 0 && res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (error) {
      console.error("Error connecting to Google Drive:", error);
      messageApi.error({
        content: t("Failed to connect to Google Drive."),
      });
    }
  };

  const backtoBaseLvl = () => {
    navigationIdRef.current++;
    const driveState = getGoogleDriveState();
    getFiles(driveState);
  };

  const goToFolderBreadcrumb = async (route: string) => {
    if (navigating) return;
    const driveState = getGoogleDriveState();
    const currentNavId = ++navigationIdRef.current;
    setNavigating(true);
    let newStrucutre = [];
    let index = folderStructure.findIndex((item: any) => item.route === route);
    if (index !== -1) {
      newStrucutre = folderStructure.slice(0, index + 1);
    }

    dispatch(updateFolderStructure(newStrucutre));
    try {
      const res = await getFilesListFromGoogleDrive(driveState, {
        page_size: 20,
        page_token: "",
        search_query: "'" + route + "' in parents",
      });
      if (currentNavId !== navigationIdRef.current) return;
      if (res.data.code === 0) {
        dispatch(updateCurrList(res.data.files));
        dispatch(updateNextPageToken(res.data.next_page_token));
      }
    } catch (err) {
      if (currentNavId !== navigationIdRef.current) return;
      setIsConnected(false);
      stateError();
      navigate("/cloud");
    } finally {
      setNavigating(false);
    }
  };

  const stateError = () => {
    messageApi.error({
      content: t("Please connect to Google Drive!"),
    });
  };

  useEffect(() => {
    if (state) {
      getFiles(state);
    } else if (prevState) {
      getFiles(prevState);
    } else {
      setInitialLoading(false);
      setIsConnected(false);
    }
  }, [state]);

  return (
    <>
      {contextHolder}
      {!isConnected && !initialLoading && (
        <Button type="primary" onClick={handleConnect}>
          {t("Connect to Google Drive")}
        </Button>
      )}
      {initialLoading && (
        <div className="loading-wrapper">
          <Spin
            indicator={
              <LoadingOutlined
                spin
                style={{ fontSize: 72, marginBottom: ".5em" }}
              />
            }
          />
          <Space />
          <div>{t("Fetching files...")}</div>
        </div>
      )}
      {isConnected && !initialLoading && (
        <>
          {googleList ? (
            <GoogleCloudFileList
              backtoBaseLvlFn={backtoBaseLvl}
              changeListFn={() => setInstanceModalState(true)}
              fileList={fileList}
              fileListLoading={fileListLoading}
              folderStructure={folderStructure}
              getNextPageFilesFn={getNextPageFiles}
              goToFolderBreadcrumbFn={goToFolderBreadcrumb}
              uploadFileFn={() => setFileUploadModal(true)}
              nextPageToken={nextPageToken}
            />
          ) : (
            <DockerFileList
              dockerList={dockerList}
              goBacktoGoogleList={() => setGoogleList(true)}
            />
          )}
        </>
      )}
      <FileUploadModal
        modalState={fileUploadModal}
        onCancelfn={() => setFileUploadModal(false)}
      />
      <InstanceSelectModal
        modalState={instanceModalState}
        switchtoDockerListFn={() => setGoogleList(false)}
        onCancelfn={() => setInstanceModalState(false)}
      />
      {navigating && <LoadingModal />}
    </>
  );
};
